import { useEffect } from 'preact/hooks';
import { useRouter } from 'preact-router';
import { useDispatch } from 'react-redux';

import { useIsMobileSize } from '@/hooks';
import { useSelector } from '@/redux/helper';
import { startTour } from '@/redux/slices/tours/actions';
import { selectTourGate, selectToursReady } from '@/redux/slices/tours/selectors';

import { trackTourStart } from '../analytics';
import { useTourContext } from '../context';
import shouldAutoStart from '../core/autoStart';
import useTourArbiter from '../hooks/useTourArbiter';
import { TOURS } from '../registry';
import TourRunner from './TourRunner';

/**
 * Mounted inside the Router. Decides WHICH tour runs: auto-starts any eligible, not-yet-started
 * tour whose `autoStart` predicate passes, then hands the arbitration winner to a runner.
 * Contains no per-tour logic — adding a tour means a registry entry, not a change here.
 * Renders nothing on mobile until there is a mobile tour UI.
 */
const TourHost = () => {
  const dispatch = useDispatch();
  const [{ url }] = useRouter();
  const isMobile = useIsMobileSize();
  // Nothing starts until the session is resolved: user.tours arrives with it, and auto-starting
  // against rehydrated-but-unmerged progress would restart a tour finished on another device.
  const isReady = useSelector(selectToursReady);
  // Subscribed so completing a tour (or syncing completion from another device) re-runs
  // auto-start without waiting for a route change.
  const gate = useSelector(selectTourGate);

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  // Tours whose eligibility depends on page data cannot be decided on navigation alone — the
  // habitat's schedule resolves asynchronously, so their gate answers `pending` at first. The
  // context is therefore a dependency here, not just of the arbiter: republishing it is what
  // re-runs this check once the answer is real.
  const context = useTourContext();

  useEffect(() => {
    if (!isReady) return;
    TOURS.forEach((tour) => {
      if (!shouldAutoStart(tour, gate, pathname, context)) return;
      trackTourStart(tour.id);
      dispatch(startTour({ tourId: tour.id, firstStepId: tour.firstStepId }));
    });
  }, [isReady, url, pathname, context, gate, dispatch]);

  const winner = useTourArbiter(pathname);

  // No mobile tour UI yet. Mount nothing at all rather than hiding it behind the mobile
  // warning — otherwise banners stay mounted and autoAdvance timers keep advancing the
  // user's tour behind the modal. <TourMobileGate> shows the "use desktop" message.
  if (isMobile) return null;

  if (!isReady || !winner) return null;

  return <TourRunner tourId={winner.id} />;
};

export default TourHost;
